import * as React from 'react';
import { 
    Box, 
    Typography, 
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import { STYLES, TokenValidator } from '../../../../../utils';
import { Orders, Logger } from '../../../../../controllers';
import { Confirmation } from '../../../../utils';
import EditOrderInfoDialog from './editOrderInfoDialog';
import CurrentTracking from './currentTracking';

const ADMIN_ROLE = 4;

const readableNote = (value) => {
    if(typeof value !== 'string') return 'No notes available';
    const note = value.trim();
    return note.length > 0 ? note : 'No notes available';
};

export default function Order({orderInfo, reload}) {

    const [cancelConfirm, showCancelConfirm] = React.useState(false);
    const [stopConfirm, showStopConfirm] = React.useState(false);
    const [releaseConfirm, showReleaseConfirm] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);
    const [canEdit, setCanEdit] = React.useState(false);
    const notes = orderInfo?.data_general?.notes;
    const docNotes = readableNote(notes?.from_dc);
    const labNotes = readableNote(notes?.from_lab);

    React.useEffect(() => {
        let active = true;
        const checkPermission = async() => {
            try {
                const token = await TokenValidator.GetInternalUser();
                if (!token || !(await TokenValidator.ValidTokenDate(token))) return;
                const decoded = await TokenValidator.SimplifyToken(token);
                if (active) setCanEdit((Number(decoded.roles) & ADMIN_ROLE) === ADMIN_ROLE);
            } catch (_) {
                if (active) setCanEdit(false);
            }
        };
        checkPermission();
        return () => { active = false; };
    }, []);

    const handleCancel = async() => {
        showCancelConfirm(false);

        const payload = {
            order: orderInfo.id
        };

        const token = await TokenValidator.GetInternalUser();

        const respose = await Orders.Cancelation(payload, token);

        if(!respose.success){
            alert('Error while canceling order. Please, try again.');
            return;
        }

        const log = {
            type: 'Update',
            title: 'Cancelation',
            information: 'Order was canceled'
        }

        await Logger.Order(orderInfo.id, log);
        reload();        
        alert('Order canceled successfullly.');

    }

    const handleStop = async() => {
        showStopConfirm(false);

        const payload = {
            status: 'Problem',
            order: orderInfo.id
        };
        
        const token = await TokenValidator.GetInternalUser();
        const respose = await Orders.UpdateStatus(payload, token);

        if(!respose.success){
            alert('Error while stopping order. Please, try again.');
            return;
        }

        const log = {
            type: 'Update',
            title: 'Stop',
            information: 'Order was stopped'
        }

        await Logger.Order(orderInfo.id, log);
        reload();        
        alert('Order stopped successfullly.');
    }

    const handleRelease = async() => {
        showReleaseConfirm(false);

        const payload = {
            status: 'Production',
            order: orderInfo.id
        };
        
        const token = await TokenValidator.GetInternalUser();
        const respose = await Orders.UpdateStatus(payload, token);

        if(!respose.success){
            alert('Error while releasing order. Please, try again.');
            return;
        }

        const log = {
            type: 'Update',
            title: 'Release',
            information: 'Order was released'
        }

        await Logger.Order(orderInfo.id, log);
        reload();
        alert('Order released successfullly.');
    }

    const saveBlob = (blob, name) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    }

    const handleDownloadZip = async() => {
        const response = await Orders.DownloadFilesZip(orderInfo.id, 'all');

        if(!response || !response.ok){
            alert('No files available to download.');
            return;
        }

        const blob = await response.blob();
        saveBlob(blob, `order_${orderInfo.id}_all.zip`);

        await Logger.Order(orderInfo.id, {
            type: 'Update',
            title: 'Files',
            information: 'Files downloaded (.zip)'
        });
        reload();
    }

    const handleUpload = async(e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';

        if(files.length < 1){
            return;
        }

        if(!window.confirm(`Upload/replace ${files.length} file(s) in this order?\nFiles with the same name will be overwritten.`)){
            return;
        }

        const formData = new FormData();
        files.forEach(f => formData.append('files', f));

        const token = await TokenValidator.GetInternalUser();
        const response = await Orders.UploadFiles(orderInfo.id, formData, token);

        if(!response || !response.success){
            alert(`Error uploading files. ${response && response.message ? response.message : ''}`);
            return;
        }

        const up = response.result ? response.result.uploaded.length : files.length;
        const fail = (response.result && response.result.failed) ? response.result.failed.length : 0;

        await Logger.Order(orderInfo.id, {
            type: 'Update',
            title: 'Files',
            information: `Files uploaded/replaced (${up}/${files.length})`
        });

        alert(`Uploaded ${up}/${files.length}${fail ? ` (failed ${fail})` : ''}.`);
        reload();
    }

    return(
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Confirmation 
                open={cancelConfirm} 
                onClose={() => showCancelConfirm(false)} 
                onConfirm={handleCancel} 
                title={'Order Cancelation'} 
                msg={`This will stop the order from proccess through production.\nDo you wanna proceed?`}
            />

            <Confirmation 
                open={stopConfirm} 
                onClose={() => showStopConfirm(false)} 
                onConfirm={handleStop} 
                title={'Order Stop'} 
                msg={`This will stop the order from proccess through production.\nDo you wanna proceed?`}
            />

            <Confirmation 
                open={releaseConfirm} 
                onClose={() => showReleaseConfirm(false)} 
                onConfirm={handleRelease} 
                title={'Order Release'} 
                msg={`This will allow the order from proccess through production.\nDo you wanna proceed?`}
            />

            <EditOrderInfoDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                order={orderInfo}
                onUpdated={reload}
            />

            <Box
                sx={{
                    minWidth:'100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor:'#1976d2',
                    position: 'relative'
                }}
            >
                <Typography variant='h6' color='white'>ORDER INFO</Typography>
                {canEdit && (
                    <Tooltip title='Edit Order Info'>
                        <IconButton
                            size='small'
                            color='inherit'
                            aria-label='Edit Order Info'
                            onClick={() => setEditOpen(true)}
                            sx={{position: 'absolute', right: 8, color: 'white'}}
                        >
                            <EditIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
                <br />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'Row',
                    gap:2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>ORDER ID</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.id}</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>CASE ID</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.case_serial}</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>ORDER REF</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.reference_id}</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>ORDER CLIENT</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.data_general.case_client}</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>ORDER TYPE</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.data_general.case_type}</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>ORDER FGS</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.fgs}</Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>ORDER STATUS</Typography>
                    <Divider sx={{minWidth:'100%'}}></Divider>
                    <Typography >{orderInfo.status}</Typography>
                </Box>

            </Box>
            
            <br/>
                    
            <Box
                sx={{
                    minWidth:'100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor:'#1976d2'
                }}
            >
                <Typography variant='h6' color='white'>ACTIONS</Typography>
            </Box>
                <br />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'Row',
                    gap:2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: 1,
                        borderRadius: 4,
                        flexGrow: 1,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>CANCEL ORDER</Typography>
                    <Button variant='outlined' color="secondary" onClick={() => showCancelConfirm(true)}>EXCECUTE</Button>
                </Box>
                
                {orderInfo.status_id !== 4 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: 1,
                            borderRadius: 4,
                            flexGrow: 1,
                            borderColor: 'lightgray',
                            padding:2
                        }}
                    >
                        <Typography variant='h7'>STOP ORDER</Typography>
                        <Button variant='outlined' color="secondary" onClick={() => showStopConfirm(true)}>EXCECUTE</Button>
                    </Box>
                )}

                {orderInfo.status_id === 4 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: 1,
                            borderRadius: 4,
                            flexGrow: 1,
                            borderColor: 'lightgray',
                            padding:2
                        }}
                    >
                        <Typography variant='h7'>RELEASE ORDER</Typography>
                        <Button variant='contained' color="success" onClick={() => showReleaseConfirm(true)}>EXCECUTE</Button>
                    </Box>
                )}

            </Box>
            
            <br/>

            <Box
                sx={{
                    minWidth:'100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor:'#1976d2'
                }}
            >
                <Typography variant='h6' color='white'>FILES</Typography>
            </Box>
                <br />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'Row',
                    gap:2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: 1,
                        borderRadius: 4,
                        flexGrow: 1,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>DOWNLOAD FILES</Typography>
                    <Button variant='outlined' color="secondary" onClick={handleDownloadZip}>DOWNLOAD ALL (.zip)</Button>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: 1,
                        borderRadius: 4,
                        flexGrow: 1,
                        borderColor: 'lightgray',
                        padding:2
                    }}
                >
                    <Typography variant='h7'>REPLACE FILES (whole folder)</Typography>
                    <Button variant='contained' component='label' color="primary">
                        UPLOAD / REPLACE FILES
                        <input hidden type='file' multiple accept='.stl,.pts,.svg,.prg' onChange={handleUpload}/>
                    </Button>
                </Box>

            </Box>

            <br/>

            <Box
                sx={{
                    minWidth:'100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor:'#1976d2'
                }}
            >
                <Typography variant='h6' color='white'>ORDER NOTES</Typography>
            </Box>
            <br />
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box'
                }}
            >
                <Box
                    sx={{
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding: 2,
                        minWidth: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'hidden'
                    }}
                >
                    <Typography variant='subtitle1' fontWeight='bold'>DOC NOTES</Typography>
                    <Typography
                        variant='body2'
                        sx={{
                            mt: 1,
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word'
                        }}
                    >
                        {docNotes}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        border: 1,
                        borderRadius: 4,
                        borderColor: 'lightgray',
                        padding: 2,
                        minWidth: 0,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'hidden'
                    }}
                >
                    <Typography variant='subtitle1' fontWeight='bold'>LAB NOTES</Typography>
                    <Typography
                        variant='body2'
                        sx={{
                            mt: 1,
                            maxWidth: '100%',
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word'
                        }}
                    >
                        {labNotes}
                    </Typography>
                </Box>
            </Box>

            <br/>

            <CurrentTracking orderId={orderInfo.id}/>
            <Typography variant='h6' >ORDER HISTORY</Typography>
            <Divider sx={{minWidth:'100%'}}></Divider>
            
            <TableContainer sx={{my:2}}>
                <Table
                    size='small'
                >
                    <TableHead>
                        <TableRow>
                            <TableCell 
                                align='center'
                                sx={STYLES.Table001.HeaderSearch}
                            >
                                Event
                            </TableCell>
                            <TableCell 
                                align='center'
                                sx={STYLES.Table001.HeaderSearch}
                            >
                                Info
                            </TableCell>
                            <TableCell 
                                align='center'
                                sx={STYLES.Table001.HeaderSearch}
                            >
                                Date
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orderInfo.logs.length > 0 && orderInfo.logs.map((row) => (
                            <TableRow>
                                <TableCell align='center'>
                                    {row.data.title}
                                </TableCell>
                                <TableCell >
                                    {row.data.information}
                                </TableCell>
                                <TableCell align='center'>
                                    {new Date(row.epoc * 1000).toLocaleString()}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {orderInfo.logs.length < 1 && (
                    <Typography variant='h7'>
                        No records found
                    </Typography>
                )}
            </TableContainer>
        </Box>
    );
}
